import React, { useState, useEffect, useCallback } from 'react';
import PrimaryNavigation from 'components/ui/PrimaryNavigation';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import CategorySlider from './components/CategorySlider';
import BudgetTemplates from './components/BudgetTemplates';
import BudgetSummary from './components/BudgetSummary';
import MonthSelector from './components/MonthSelector';
import AddCategoryModal from './components/AddCategoryModal';
import AiBudgetOptimizer from './components/AiBudgetOptimizer';

const DEFAULT_CATEGORIES = [
  'Groceries', 'Rent', 'Transportation', 'Entertainment', 'Shopping', 'Dining', 'Healthcare', 'Other',
];

const DEFAULT_ALLOCATIONS = {
  Groceries: 400, Rent: 1200, Transportation: 200, Entertainment: 150,
  Shopping: 200, Dining: 250, Healthcare: 100, Other: 150,
};

const CATEGORY_ICONS = {
  Groceries: 'ShoppingCart', Rent: 'Home', Transportation: 'Car', Entertainment: 'Tv',
  Shopping: 'ShoppingBag', Dining: 'UtensilsCrossed', Healthcare: 'Heart', Other: 'Tag',
};

const CATEGORY_COLORS = {
  Groceries: '#10B981', Rent: '#06B6D4', Transportation: '#7C3AED', Entertainment: '#EC4899',
  Shopping: '#EF4444', Dining: '#F59E0B', Healthcare: '#3B82F6', Other: '#64748B',
};

function getCurrentMonth() {
  const d = new Date();
  return `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}`;
}

export default function BudgetManager() {
  const { user } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [categories, setCategories] = useState([]);
  const [budgetRecord, setBudgetRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [totalBudget, setTotalBudget] = useState(3000);
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState('3000');
  // AI category insights (behavioral nudges per category)
  const [aiCategoryInsights, setAiCategoryInsights] = useState({});

  // Get or create a budget record for the selected month
  const getOrCreateBudget = useCallback(async () => {
    if (!user) return null;
    // Try to find existing budget for this month
    const { data: existing } = await supabase
      ?.from('budgets')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.eq('month_year', selectedMonth)
      ?.maybeSingle();

    if (existing) return existing;

    // Create new budget record for this month
    const { data: created, error } = await supabase
      ?.from('budgets')
      ?.insert({ user_id: user?.id, month_year: selectedMonth, total_budget: totalBudget, monthly_income: totalBudget })
      ?.select()
      ?.single();

    if (error) {
      console.error('Error creating budget record:', error);
      return null;
    }
    return created;
  }, [user, selectedMonth, totalBudget]);

  const loadBudgets = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const budget = await getOrCreateBudget();
      if (!budget) throw new Error('Could not get budget record');

      setBudgetRecord(budget);
      setTotalBudget(budget?.total_budget || 3000);
      setBudgetInput(String(budget?.total_budget || 3000));

      // Load spending categories linked to this budget
      const { data: cats, error: catsError } = await supabase
        ?.from('spending_categories')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('budget_id', budget?.id);

      if (catsError) throw catsError;

      if (cats && cats?.length > 0) {
        // Map spending_categories fields to component-expected shape
        setCategories(cats?.map((c) => ({
          id: c?.id,
          user_id: c?.user_id,
          budget_id: c?.budget_id,
          category: c?.name,
          allocated_amount: c?.monthly_limit || 0,
          spent_amount: c?.spent || 0,
          weekly_limit: null,
          alert_enabled: false,
          rollover: false,
          is_custom: false,
          icon: c?.icon,
          color: c?.color,
        })));
      } else {
        // Initialize with defaults
        const defaults = DEFAULT_CATEGORIES?.map((cat) => ({
          id: null,
          user_id: user?.id,
          budget_id: budget?.id,
          category: cat,
          allocated_amount: DEFAULT_ALLOCATIONS?.[cat] || 100,
          spent_amount: 0,
          weekly_limit: null,
          alert_enabled: false,
          rollover: false,
          is_custom: false,
          icon: CATEGORY_ICONS?.[cat] || 'Tag',
          color: CATEGORY_COLORS?.[cat] || '#64748B',
        }));
        setCategories(defaults);
      }
    } catch (err) {
      console.error('Error loading budgets:', err);
      const defaults = DEFAULT_CATEGORIES?.map((cat) => ({
        id: null,
        user_id: user?.id,
        budget_id: null,
        category: cat,
        allocated_amount: DEFAULT_ALLOCATIONS?.[cat] || 100,
        spent_amount: 0,
        weekly_limit: null,
        alert_enabled: false,
        rollover: false,
        is_custom: false,
        icon: CATEGORY_ICONS?.[cat] || 'Tag',
        color: CATEGORY_COLORS?.[cat] || '#64748B',
      }));
      setCategories(defaults);
    } finally {
      setLoading(false);
    }
  }, [user, selectedMonth, getOrCreateBudget]);

  useEffect(() => {
    loadBudgets();
  }, [loadBudgets]);

  const saveAll = async (updatedCategories, budgetId) => {
    if (!user) return;
    const bid = budgetId || budgetRecord?.id;
    if (!bid) return;
    setSaving(true);
    try {
      for (const cat of updatedCategories) {
        const payload = {
          user_id: user?.id,
          budget_id: bid,
          name: cat?.category,
          icon: cat?.icon || CATEGORY_ICONS?.[cat?.category] || 'Tag',
          color: cat?.color || CATEGORY_COLORS?.[cat?.category] || '#64748B',
          monthly_limit: cat?.allocated_amount || 0,
          spent: cat?.spent_amount || 0,
        };
        if (cat?.id) {
          await supabase?.from('spending_categories')?.update(payload)?.eq('id', cat?.id);
        } else {
          const { data } = await supabase?.from('spending_categories')?.insert(payload)?.select()?.single();
          if (data) cat.id = data?.id;
        }
      }
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const saveTotalBudget = async (amount) => {
    if (!user || !budgetRecord?.id) return;
    await supabase?.from('budgets')?.update({ total_budget: amount, monthly_income: amount })?.eq('id', budgetRecord?.id);
  };

  const updateCategory = (index, field, value) => {
    const updated = categories?.map((c, i) => i === index ? { ...c, [field]: value } : c);
    setCategories(updated);
    saveAll(updated);
  };

  const deleteCategory = async (index) => {
    const cat = categories?.[index];
    if (cat?.id) {
      await supabase?.from('spending_categories')?.delete()?.eq('id', cat?.id);
    }
    setCategories((prev) => prev?.filter((_, i) => i !== index));
  };

  const handleAddCategory = async ({ name, allocated, weeklyLimit }) => {
    const newCat = {
      id: null,
      user_id: user?.id,
      budget_id: budgetRecord?.id,
      category: name,
      allocated_amount: allocated,
      spent_amount: 0,
      weekly_limit: weeklyLimit,
      alert_enabled: false,
      rollover: false,
      is_custom: true,
      icon: 'Tag',
      color: '#64748B',
    };
    const updated = [...categories, newCat];
    setCategories(updated);
    await saveAll(updated);
  };

  const applyTemplate = (allocations) => {
    const updated = categories?.map((cat) => ({
      ...cat,
      allocated_amount: allocations?.[cat?.category] || cat?.allocated_amount,
    }));
    setCategories(updated);
    saveAll(updated);
  };

  // Apply a single AI suggestion to a category
  const handleApplySuggestion = async (categoryName, suggestedBudget) => {
    const idx = categories?.findIndex((c) => c?.category === categoryName);
    if (idx === -1) return;
    const updated = categories?.map((c, i) =>
      i === idx ? { ...c, allocated_amount: suggestedBudget } : c
    );
    setCategories(updated);
    await saveAll(updated);
  };

  // Apply all AI suggestions at once
  const handleApplyAll = async (suggestions) => {
    if (!suggestions?.length) return;
    const suggMap = {};
    suggestions?.forEach((s) => { suggMap[s?.category] = s?.suggested_budget; });
    const updated = categories?.map((c) => ({
      ...c,
      allocated_amount: suggMap?.[c?.category] !== undefined ? suggMap?.[c?.category] : c?.allocated_amount,
    }));
    setCategories(updated);
    await saveAll(updated);
  };

  return (
    <div className="min-h-screen bg-[#0D1B2A]">
      <PrimaryNavigation />
      <main className="pt-6 pb-24 md:pb-8 px-4 md:px-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#F8FAFC] flex items-center gap-2">
              <Icon name="Wallet" size={24} color="#00E5FF" />
              Budget Manager
            </h1>
            <p className="text-[#64748B] text-sm mt-1">Manage your monthly budget allocations</p>
          </div>
          <div className="flex items-center gap-3">
            <MonthSelector selectedMonth={selectedMonth} onChange={setSelectedMonth} />
            {saving && (
              <div className="flex items-center gap-1.5 text-xs text-[#64748B]">
                <div className="w-3 h-3 border border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
                Saving...
              </div>
            )}
          </div>
        </div>

        {/* Total Budget Input */}
        <div className="bg-[rgba(0,229,255,0.05)] border border-[rgba(0,229,255,0.15)] rounded-xl p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="DollarSign" size={20} color="#00E5FF" />
            <span className="text-[#E2E8F0] font-medium">Monthly Income / Total Budget</span>
          </div>
          {editingBudget ? (
            <div className="flex items-center gap-2">
              <span className="text-[#00E5FF] font-mono">$</span>
              <input
                type="number"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e?.target?.value)}
                className="w-28 bg-[rgba(255,255,255,0.08)] border border-[#00E5FF] rounded-lg px-2 py-1 text-[#00E5FF] font-mono text-sm focus:outline-none"
                autoFocus
              />
              <button
                onClick={() => {
                  const amount = Number(budgetInput);
                  setTotalBudget(amount);
                  setEditingBudget(false);
                  saveTotalBudget(amount);
                }}
                className="text-[#10B981] hover:text-[#34D399] transition-colors"
              >
                <Icon name="Check" size={16} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setBudgetInput(String(totalBudget)); setEditingBudget(true); }}
              className="flex items-center gap-2 text-[#00E5FF] font-mono font-bold text-lg hover:opacity-80 transition-opacity"
            >
              ${totalBudget?.toLocaleString()}
              <Icon name="Pencil" size={14} color="#64748B" />
            </button>
          )}
        </div>

        {/* Summary Cards */}
        <BudgetSummary categories={categories} totalBudget={totalBudget} />

        {/* AI Budget Optimizer — below month selector / summary */}
        <AiBudgetOptimizer
          categories={categories}
          selectedMonth={selectedMonth}
          totalBudget={totalBudget}
          onApplySuggestion={handleApplySuggestion}
          onApplyAll={handleApplyAll}
          onCategoryInsights={setAiCategoryInsights}
        />

        {/* Templates */}
        <div className="mb-6">
          <BudgetTemplates totalBudget={totalBudget} onApply={applyTemplate} />
        </div>

        {/* Categories Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 rounded-full border-2 border-[#00E5FF] border-t-transparent animate-spin" />
              <p className="text-[#64748B] text-sm">Loading budget data...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[#E2E8F0] font-semibold flex items-center gap-2">
                <Icon name="LayoutGrid" size={16} color="#7B2FBE" />
                Category Allocations
                <span className="text-xs text-[#64748B] font-normal">({categories?.length} categories)</span>
              </h2>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[rgba(123,47,190,0.15)] border border-[rgba(123,47,190,0.3)] text-[#7B2FBE] text-xs font-medium hover:bg-[rgba(123,47,190,0.25)] transition-colors"
              >
                <Icon name="Plus" size={14} />
                Add Category
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {categories?.map((cat, idx) => (
                <div key={`${cat?.category}-${idx}`}>
                  <CategorySlider
                    category={cat?.category}
                    allocated={cat?.allocated_amount}
                    spent={cat?.spent_amount}
                    weeklyLimit={cat?.weekly_limit}
                    alertEnabled={cat?.alert_enabled}
                    rollover={cat?.rollover}
                    isCustom={cat?.is_custom}
                    totalBudget={totalBudget}
                    onAllocatedChange={(val) => updateCategory(idx, 'allocated_amount', val)}
                    onWeeklyLimitChange={(val) => updateCategory(idx, 'weekly_limit', val)}
                    onAlertToggle={() => updateCategory(idx, 'alert_enabled', !cat?.alert_enabled)}
                    onRolloverToggle={() => updateCategory(idx, 'rollover', !cat?.rollover)}
                    onDelete={() => deleteCategory(idx)}
                  />
                  {/* AI Behavioral Nudge */}
                  {aiCategoryInsights?.[cat?.category] && (
                    <div className="mt-1.5 px-1 flex items-center gap-1.5">
                      <Icon name="Sparkles" size={11} color="#F59E0B" />
                      <span
                        className="text-[11px] leading-tight"
                        style={{
                          color: aiCategoryInsights?.[cat?.category]?.toLowerCase()?.includes('overspend') ||
                                 aiCategoryInsights?.[cat?.category]?.toLowerCase()?.includes('over budget')
                            ? '#F59E0B' :'#10B981',
                        }}
                      >
                        {aiCategoryInsights?.[cat?.category]}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {showAddModal && (
          <AddCategoryModal
            onClose={() => setShowAddModal(false)}
            onAdd={handleAddCategory}
          />
        )}
      </main>
    </div>
  );
}
